import {ServiceNowNavMenuResponse} from './api';

/**
 * The last Knowledge menu this person loaded, so the left nav and /content
 * paint at once on a new page load while nav-menu (seconds: it asks
 * ServiceNow which restricted articles they can read) refreshes behind it.
 * Topic titles and links only, never article bodies. Per person, versioned, a
 * few days at most, cleared on sign-out.
 */

const STORAGE_PREFIX = 'knowledge_nav_menu_v1:';
const MAX_AGE_MS = 3 * 24 * 60 * 60 * 1000;

type NavMenuSnapshot = {
  savedAt: number;
  menu: ServiceNowNavMenuResponse;
};

const storageKey = (ldap: string) => `${STORAGE_PREFIX}${ldap.toLowerCase()}`;

const storedKeys = () =>
  Array.from({length: localStorage.length}, (_, index) => localStorage.key(index)).filter(
    (key): key is string => Boolean(key?.startsWith(STORAGE_PREFIX))
  );

export const loadNavMenuSnapshot = (
  ldap: string | null | undefined,
  now: number = Date.now()
): NavMenuSnapshot | null => {
  if (!ldap) return null;
  try {
    const key = storageKey(ldap);
    // Anyone else's (a session that ended without signing out) goes.
    storedKeys()
      .filter((other) => other !== key)
      .forEach((other) => localStorage.removeItem(other));
    const stored = localStorage.getItem(key);
    if (!stored) return null;
    const snapshot = JSON.parse(stored) as NavMenuSnapshot;
    if (now - snapshot.savedAt > MAX_AGE_MS || !Array.isArray(snapshot.menu?.categories)) {
      localStorage.removeItem(key);
      return null;
    }
    return snapshot;
  } catch {
    // Unreadable (or storage unavailable): drop it.
    try {
      localStorage.removeItem(storageKey(ldap));
    } catch {
      /* storage unavailable */
    }
    return null;
  }
};

/** Save the menu just loaded, replacing anyone else's on this browser. */
export const saveNavMenuSnapshot = (
  ldap: string | null | undefined,
  menu: ServiceNowNavMenuResponse,
  now: number = Date.now()
) => {
  if (!ldap) return;
  try {
    clearNavMenuSnapshots();
    localStorage.setItem(
      storageKey(ldap),
      JSON.stringify({savedAt: now, menu} satisfies NavMenuSnapshot)
    );
  } catch {
    /* storage unavailable or full: next visit just loads */
  }
};

/** Drop every saved menu (sign-out). */
export const clearNavMenuSnapshots = () => {
  try {
    storedKeys().forEach((key) => localStorage.removeItem(key));
  } catch {
    /* storage unavailable */
  }
};
